/**
 * SanPlumb — Logo principal (variante isométrica)
 *
 * Marca de la app: el SVG dibuja una red isométrica miniatura con AF azul +
 * AC rojo y los puntos de derivación/válvulas en ámbar y dorado, frente al
 * wordmark "SANPLUMB" en Syne. Animación de entrada:
 *   1. Las líneas se "dibujan" con stroke-dashoffset (cubic-bezier suave).
 *   2. Los puntos (codos, tee, válvulas) aparecen con un "pop" elástico.
 *
 * Variantes:
 *   - <DashLogo />  Tamaño Dashboard (180×130 svg + título 96px).
 *   - <BootLogo />  Tamaño splash (140×100 svg + título 56px).
 *
 * El componente es puro (no recibe props): toda la animación vive en CSS
 * (App.css, bloque "Variant ISO") para que el re-mount al cambiar de pantalla
 * relance la animación sin tocar React state.
 */

import type { CSSProperties } from 'react';

interface BrandElbowProps {
  className?: string;
  gradientId?: string;
}

export function BrandElbow({
  className = 'brand-elbow-svg',
  gradientId = 'brand-elbgrad',
}: BrandElbowProps): JSX.Element {
  return (
    <svg className={className} viewBox="0 0 80 80" aria-hidden="true">
      <defs>
        <linearGradient id={gradientId} x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="#00C2FF" />
          <stop offset="100%" stopColor="#FF4040" />
        </linearGradient>
      </defs>
      <path
        d="M 8 8 L 40 8 Q 72 8 72 40 L 72 72"
        fill="none"
        stroke={`url(#${gradientId})`}
        strokeWidth="10"
        strokeLinecap="round"
      />
      <circle cx="40" cy="40" r="8" fill="#FFB800" />
      <circle cx="40" cy="40" r="3" fill="#040B13" />
    </svg>
  );
}

export function DashLogo(): JSX.Element {
  return (
    <div className="dl-iso">
      <svg className="dl-iso-net" viewBox="0 0 200 140" aria-hidden="true">
        {/* Recinto del baño (footprint) */}
        <rect
          x="10"
          y="100"
          width="40"
          height="28"
          fill="rgba(0,194,255,0.10)"
          stroke="#00C2FF"
          strokeWidth="1.5"
        />
        {/* Montante AF que sube */}
        <line
          className="iso-riser"
          x1="30"
          y1="100"
          x2="30"
          y2="40"
          stroke="#00C2FF"
          strokeWidth="3"
          strokeLinecap="round"
        />
        {/* Main AF horizontal */}
        <line
          className="iso-h-af"
          x1="30"
          y1="40"
          x2="170"
          y2="40"
          stroke="#00C2FF"
          strokeWidth="3"
          strokeLinecap="round"
        />
        {/* Main AC horizontal */}
        <line
          className="iso-h-ac"
          x1="50"
          y1="60"
          x2="170"
          y2="60"
          stroke="#FF4040"
          strokeWidth="3"
          strokeLinecap="round"
        />
        {/* Ramales hacia aparatos */}
        <line
          className="iso-d1"
          x1="90"
          y1="40"
          x2="90"
          y2="90"
          stroke="#00C2FF"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <line
          className="iso-d2"
          x1="130"
          y1="60"
          x2="130"
          y2="90"
          stroke="#FF4040"
          strokeWidth="2"
          strokeLinecap="round"
        />
        {/* Codos y válvulas */}
        <circle cx="30" cy="40" r="4" fill="#F4A261" />
        <circle cx="50" cy="60" r="4" fill="#FF8C42" />
        <circle cx="90" cy="40" r="3" fill="#FFB800" />
        <circle cx="130" cy="60" r="3" fill="#FFB800" />
      </svg>
      <h1 className="dl-title">
        <span className="san">SAN</span>
        <BrandElbow className="brand-elbow-svg" gradientId="brand-elbgrad-dash" />
        <span className="plumb">
          {['P', 'L', 'U', 'M', 'B'].map((ch, i) => (
            <span
              key={i}
              className="plumb-ch"
              style={{ ['--i' as keyof CSSProperties]: i } as CSSProperties}
            >
              {ch}
            </span>
          ))}
        </span>
      </h1>
    </div>
  );
}

/**
 * Logo compacto para el splash de arranque. Comparte la animación del
 * Dashboard, escalada con `.dl-boot` para encajar en el cover-overlay.
 */
export function BootLogo(): JSX.Element {
  return (
    <div className="dl-iso dl-boot">
      <svg className="dl-iso-net" viewBox="0 0 200 140" aria-hidden="true">
        <rect
          x="10"
          y="100"
          width="40"
          height="28"
          fill="rgba(0,194,255,0.10)"
          stroke="#00C2FF"
          strokeWidth="1.5"
        />
        <line
          className="iso-riser"
          x1="30"
          y1="100"
          x2="30"
          y2="40"
          stroke="#00C2FF"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <line
          className="iso-h-af"
          x1="30"
          y1="40"
          x2="170"
          y2="40"
          stroke="#00C2FF"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <line
          className="iso-h-ac"
          x1="50"
          y1="60"
          x2="170"
          y2="60"
          stroke="#FF4040"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <line
          className="iso-d1"
          x1="90"
          y1="40"
          x2="90"
          y2="90"
          stroke="#00C2FF"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <line
          className="iso-d2"
          x1="130"
          y1="60"
          x2="130"
          y2="90"
          stroke="#FF4040"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <circle cx="30" cy="40" r="4" fill="#F4A261" />
        <circle cx="50" cy="60" r="4" fill="#FF8C42" />
        <circle cx="90" cy="40" r="3" fill="#FFB800" />
        <circle cx="130" cy="60" r="3" fill="#FFB800" />
      </svg>
      <h1 className="dl-title">
        <span className="san">SAN</span>
        <BrandElbow className="brand-elbow-svg" gradientId="brand-elbgrad-boot" />
        <span className="plumb">PLUMB</span>
      </h1>
    </div>
  );
}
